/**
 * Created by neo on 18.12.20.
 */
import { makeAutoObservable } from 'mobx';
import { v4 as UUID } from 'uuid';
import { EstimatedCost, createEstimatedCost } from '../EstimatedCost';
import { LocalizedValue, createLocalizedValue } from '../../LocalizedValue';
import { NutritionInformation, createNutritionInformation } from './NutritionInformation';
import { IngredientSearchParams } from './IngredientSearchParams';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { LocalizedEntity, createLocalizedEntity } from '../../LocalizedEntity';
import { Media, createMedia } from '../../Media/Media';

export type Ingredient = LocalizedEntity & {
  id: string;
  parentIds: string[];
  spoonacularId: number;
  spoonacularParentIds: number[];
  originalName: LocalizedValue[];
  extendedName: LocalizedValue[];
  amount: number;
  unit: string;
  possibleUnits: Record<string, number>;
  estimatedCost?: EstimatedCost;
  consistency?: string;
  shoppingListUnits: string[];
  aisle?: string;
  image?: Media;
  meta: string[];
  metaInformation: string[];
  nutrition: NutritionInformation;
  categoryPath: string[];
};

export function createIngredient(json?: Partial<Ingredient>): Ingredient {
  const base = createLocalizedEntity(json);
  return makeAutoObservable({
    ...base,
    id: json?.id ?? UUID(),
    parentIds: json?.parentIds ?? [],
    spoonacularId: json?.spoonacularId ?? 0,
    spoonacularParentIds: json?.spoonacularParentIds ?? [],
    originalName: (json?.originalName ?? []).map((n: any) => createLocalizedValue(n)),
    extendedName: (json?.extendedName ?? []).map((n: any) => createLocalizedValue(n)),
    amount: json?.amount ?? 0,
    unit: json?.unit ?? '',
    possibleUnits: json?.possibleUnits ?? {},
    estimatedCost: json?.estimatedCost ? createEstimatedCost(json.estimatedCost) : undefined,
    consistency: json?.consistency,
    shoppingListUnits: json?.shoppingListUnits ?? [],
    aisle: json?.aisle,
    image: json?.image ? createMedia(json.image) : undefined,
    meta: json?.meta ?? [],
    metaInformation: json?.metaInformation ?? [],
    nutrition: createNutritionInformation(json?.nutrition),
    categoryPath: json?.categoryPath ?? [],
  });
}

export const IngredientApi = {
  search(params?: IngredientSearchParams): Promise<Ingredient[]> {
    return HttpBackend.get(`/coach-program/diet/ingredients`, params).then((result) =>
      result.map((r: any) => createIngredient(r)),
    );
  },

  get(id: string): Promise<Ingredient> {
    return HttpBackend.get(`/coach-program/diet/ingredients/${id}`).then((result) => createIngredient(result));
  },

  getAll(ids: string[]): Promise<Ingredient[]> {
    return HttpBackend.get(`/coach-program/diet/ingredients/findAll/${ids.join(',')}`).then((result) =>
      (result ?? []).map((res: any) => createIngredient(res)),
    );
  },
};
